import type { AnalyticsRange } from "@lafayette/schemas";

import { deepLinks } from "../../navigation/deep-links";

// The links between the Events lane (CHT-2030) and the Explore tab (CHT-2029), by the addresses that each tab already reads.
//
// "Zoom into this visit": the cockpit of the customer, `tab=explore`, a range, and the window of the visit as `from` and `to`. Explore reads
// `from` and `to` as its zoom. The server accepts a zoom only when it lies inside the range, ends after it starts and lasts at least one hour. So
// the window is the visit from its start to its end, made at least one hour long, and the range is the smallest one that holds it. A visit that is
// still open (no end) lasts its length so far. The end never passes the clock of the page. When the end is cut there, the START moves back, so the
// window still lasts one hour (a visit of 20 minutes that started at 11:40 gives 11:00 to 12:00). The range keeps one hour of margin: the server
// resolves it with its own clock, which is later than the clock of the page.

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** The margin that a range keeps before the start of the window, for the later clock of the server. */
const RANGE_MARGIN_MS = HOUR_MS;
/** The ranges that the cockpit knows, from the smallest. The last one, `all`, holds every visit of the 13 months. */
const RANGES: readonly (readonly [AnalyticsRange, number])[] = [
  ["24h", DAY_MS],
  ["7d", 7 * DAY_MS],
  ["30d", 30 * DAY_MS],
  ["90d", 90 * DAY_MS],
  ["12m", 365 * DAY_MS],
];

export type VisitWindow = Readonly<{
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
}>;

/** The range and the zoom that show a visit in Explore, from the clock of the page (`nowIso`). */
export function visitZoom(
  visit: VisitWindow,
  nowIso: string,
): Readonly<{ range: AnalyticsRange; from: string; to: string }> {
  const now = Date.parse(nowIso);
  const start = Date.parse(visit.startedAt);
  const end =
    visit.endedAt === null ? start + visit.durationSeconds * 1000 : Date.parse(visit.endedAt);
  // The end is at least one hour after the start, but never after the clock of the page; then the start moves back to keep one hour.
  const to = Math.min(Math.max(end, start + HOUR_MS), now);
  const from = Math.min(start, to - HOUR_MS);
  const range = RANGES.find(([, span]) => now - span + RANGE_MARGIN_MS <= from)?.[0] ?? "all";
  return { range, from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

export function exploreVisitHref(customerId: string, visit: VisitWindow, nowIso: string): string {
  const { range, from, to } = visitZoom(visit, nowIso);
  const params = new URLSearchParams({ tab: "explore", range, from, to });
  return deepLinks.customerAnalytics(customerId, `?${params.toString()}`);
}

/** The Events tab of the customer, optionally with the filter kept in the address. */
export function eventsHref(customerId: string, range: AnalyticsRange, extra = ""): string {
  return deepLinks.customerAnalytics(customerId, `?tab=events&range=${range}${extra}`);
}
